<!-- 
Shows "global" errors.

Global errors are generally unexpected errors, e.g. errors when trying to load something.
They are triggered by committing the GLOBAL_ERROR mutation with a deep copy of the error object.
Global errors appear in a red snackbar.
(Expected errors are handled differently. They are normally handled by the XXX_SAVE_FAILED mutation
and are bound to OurValidationDialog for presentation to the user.)
-->
<template>
  <div>
    <v-snackbar 
      :value="show" :multi-line="true" :timeout="timeout"
      :color="color" bottom right class="global-error"
    >
      {{ message }}
      <v-btn v-if="canViewErrorDetails" flat icon @click="showErrorDetails=true">
        <v-icon>info</v-icon>
      </v-btn>
      <v-btn v-if="error.fatal" flat @click="reload">
        Try again
      </v-btn>
      <v-btn v-else flat @click="hide">
        Close
      </v-btn>
    </v-snackbar>
    <v-dialog :value="showErrorDetails" persistent content-class="dialog sm" @keydown.esc="showErrorDetails=false">
      <v-card>
        <v-toolbar color="secondary" flat dark>
          <v-toolbar-title>Error details</v-toolbar-title>
        </v-toolbar>
        <v-card-text>
          <div style="overflow: auto; max-height: 300px;">{{ detailedMessage }}</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn flat @click="showErrorDetails=false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { GLOBAL_ERROR_HIDE } from "@/store/system/mutation-types";

const VALIDATION = "VALIDATION";
const AUTHENTICATION = "AUTHENTICATION";
const AUTHORISATION = "AUTHORISATION";
const MISSING = "MISSING";
const UNAVAILABLE = "UNAVAILABLE";
const ERROR = "ERROR";
const NORESPONSE = "NORESPONSE";
const CLIENT = "CLIENT";

export default {
  data() {
    return {
      showErrorDetails: false
    };
  },
  computed: {
    error() {
      return this.$store.state.system.globalError;
    },
    info() {
      if (this.error && this.error.details) {
        return this.error.details.response ? this.error.details.response.data : null;
      }
      return null;
    },
    show() {
      // don't show 401s, as they are intercepted and used to trigger a sign out
      return this.error.show && this.type != AUTHENTICATION;
    },
    canViewErrorDetails() {
      return this.testingToolsEnabled && (this.type === ERROR || this.type === UNAVAILABLE || this.type === MISSING);
    },
    timeout() {
      return this.type === VALIDATION ? 10000 : 0;
    },
    color() {
      return this.type === VALIDATION ? "warning" : "error";
    },
    type() {
      if (this.error && this.error.details) {
        if (this.error.details.response) {
          const status = this.error.details.response.status;
          if (status === 400) {
            return VALIDATION;
          } else if (status === 401) {
            return AUTHENTICATION;
          } else if (status === 403) {
            return AUTHORISATION;
          } else if (status === 404) {
            return MISSING;
          } else if (status === 502) {
            return UNAVAILABLE;
          } else if (status === 500 && this.isProxyError) {
            return UNAVAILABLE;
          } else {
            return ERROR;
          }
        } else if (this.error.details.request) {
          return NORESPONSE;
        } else {
          return CLIENT;
        }
      } else {
        return null;
      }
    },
    validationMessage() {
      if (this.info && this.info.errors && this.info.errors.length) {
        let messages = [];
        for (let error of this.info.errors) {
          messages = messages.concat(error.messages);
        }
        return messages.join(", ");
      }
      return null;
    },
    isProxyError() {
      return (
        this.error &&
        this.error.details &&
        this.error.details.response &&
        this.error.details.response.data &&
        this.error.details.response.data.toString().startsWith("Proxy error")
      );
    },
    message() {
      switch (this.type) {
        case NORESPONSE:
          return "This is taking a long time, sorry! Check back in a few minutes and try again if necessary.";
        case AUTHORISATION:
          return "You have insufficient permissions to perform this action";
        case AUTHENTICATION:
          return this.getRawMessage();
        case VALIDATION:
          return this.validationMessage;
        case CLIENT:
          return `Unexpected client error: ${this.getRawMessage()}`;
        case MISSING:
          return "Page not found";
        case UNAVAILABLE:
          return "LTPS is currently unavailable. Please try again later.";
        case ERROR:
        default:
          return "Unfortunately an unexpected problem has occurred. The IT team has been notified.";
      }
    },
    detailedMessage() {
      if (this.type === MISSING) {
        return this.error.details.response.config.url;
      } else if (this.type == UNAVAILABLE || this.type == ERROR) {
        return this.getRawMessage();
      } else {
        return this.message;
      }
    },
    testingToolsEnabled() {
      return this.$store.getters.testingToolsEnabled;
    }
  },
  methods: {
    reload() {
      window.location.reload(true);
    },
    hide() {
      this.$store.commit(GLOBAL_ERROR_HIDE);
    },
    getRawMessage() {
      if (this.info) {
        return this.info.title ? this.info.title : this.info;
      }
      return "An unhandled error has occurred.";
    }
  }
};
</script>
